import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GradeKpis, Kpi } from "@/features/overview/telas/Kpi";
import { CORES } from "@/features/overview/series";
import { formatDateBr } from "@/lib/format";
import { useConfiguracaoAcessos, useResumoAcessos } from "./api";
import { AbaInstituicoes } from "./AbaInstituicoes";
import { AbaTrialsChurn } from "./AbaTrialsChurn";
import { InfoAcesso, PeriodoSelect } from "./componentes";
import { usePeriodoUrl } from "./usePeriodoUrl";
import { ConfiguracaoAcessosDialog } from "./ConfiguracaoAcessosDialog";
import { Engajamento } from "./Engajamento";
import { TabelaUsuarios } from "./TabelaUsuarios";
import { descreverPeriodo, formatNumero, formatPercentual, tendencia } from "./formato";

const VISOES = [
  { id: "instituicoes", label: "Instituições" },
  { id: "usuarios", label: "Usuários" },
  { id: "engajamento", label: "Engajamento" },
  { id: "trials", label: "Trials e churn" },
] as const;

type Visao = (typeof VISOES)[number]["id"];

const isVisao = (v: string | null): v is Visao => VISOES.some((x) => x.id === v);

/** Instituições > Frequência de acessos. Período e aba ficam na URL. */
export default function FrequenciaAcessos() {
  const [params, setParams] = useSearchParams();
  const { periodo, dias, definir } = usePeriodoUrl();
  const visaoUrl = params.get("visao");
  const visao: Visao = isVisao(visaoUrl) ? visaoUrl : "instituicoes";
  const [configAberta, setConfigAberta] = useState(false);

  const resumo = useResumoAcessos(periodo);
  const config = useConfiguracaoAcessos();
  const r = resumo.data;
  const t = r ? tendencia(r.logins, r.loginsPeriodoAnterior) : undefined;

  const trocarVisao = (v: string) => {
    const p = new URLSearchParams(params);
    if (v === "instituicoes") p.delete("visao");
    else p.set("visao", v);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-foreground">Frequência de acessos</h1>
          <InfoAcesso />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodoSelect key={`${periodo.inicio}-${periodo.fim}`} periodo={periodo} dias={dias} onChange={definir} />
          <Button variant="outline" onClick={() => setConfigAberta(true)}>
            <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden /> Configurar limites
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Ativas + Trial, {descreverPeriodo(periodo, dias)}.
        {config.data &&
          ` Instituição em atenção com ${config.data.diasAtencaoInstituicao}+ dias sem acesso e inativa com ${config.data.diasInativaInstituicao}+; usuário com ${config.data.diasAtencaoUsuario}+ e ${config.data.diasInativoUsuario}+.`}
        {r?.inicioHistorico && ` Histórico de logins desde ${formatDateBr(r.inicioHistorico)}.`}
      </p>

      <GradeKpis variante="padrao" colunas={5}>
        <Kpi
          variante="padrao"
          rotulo="Instituições com acesso no período"
          valor={r ? `${formatNumero(r.instituicoesComAcessoPeriodo)} de ${formatNumero(r.instituicoes)}` : "—"}
          detalhe={r ? `${formatNumero(r.instituicoesComAcessoHoje)} acessaram hoje` : undefined}
          cor={CORES.azul}
          isLoading={resumo.isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Instituições inativas"
          valor={formatNumero(r?.instituicoesInativas)}
          detalhe={r ? `${formatNumero(r.instituicoesAtencao)} em atenção · ${formatNumero(r.instituicoesNunca)} nunca acessaram` : undefined}
          cor={CORES.vermelho}
          isLoading={resumo.isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Usuários que acessaram"
          valor={r ? `${formatNumero(r.usuariosAcessaramPeriodo)} de ${formatNumero(r.usuarios)}` : "—"}
          detalhe={r?.usuarios ? `${formatPercentual(r.usuariosAcessaramPeriodo / r.usuarios)} dos usuários ativos` : undefined}
          cor={CORES.verde}
          isLoading={resumo.isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Novos usuários"
          valor={formatNumero(r?.usuariosNovos)}
          detalhe={r ? `${formatNumero(r.usuariosReativados)} reativados · ${formatNumero(r.instituicoesReativadas)} instituições reativadas` : undefined}
          cor={CORES.roxo}
          isLoading={resumo.isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Logins no período"
          valor={formatNumero(r?.logins)}
          detalhe={
            r && t
              ? `${t.texto} vs. período anterior · ${r.logins ? formatPercentual(r.loginsMobile / r.logins) : "0%"} pelo app`
              : undefined
          }
          isLoading={resumo.isLoading}
        />
      </GradeKpis>

      <Tabs value={visao} onValueChange={trocarVisao} className="space-y-4">
        <TabsList>
          {VISOES.map((v) => (
            <TabsTrigger key={v.id} value={v.id}>
              {v.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="instituicoes" className="mt-0">
          <AbaInstituicoes periodo={periodo} />
        </TabsContent>
        <TabsContent value="usuarios" className="mt-0">
          <TabelaUsuarios periodo={periodo} />
        </TabsContent>
        <TabsContent value="engajamento" className="mt-0">
          <Engajamento periodo={periodo} />
        </TabsContent>
        <TabsContent value="trials" className="mt-0">
          <AbaTrialsChurn periodo={periodo} />
        </TabsContent>
      </Tabs>

      <ConfiguracaoAcessosDialog open={configAberta} onOpenChange={setConfigAberta} />
    </div>
  );
}
